'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreditCard,
  Download,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useAppContext } from '@/shared/contexts/app';
import { trackPhotoEffects } from '@/shared/lib/analytics';

interface FaceSwapProps {
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
}

interface BackendTask {
  id: string;
  status: string;
  taskInfo: string | null;
}

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;
const COST_CREDITS = 1;

const MODEL = 'half-moon-ai/ai-face-swap/faceswapimage';
const PROVIDER = 'fal';

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function FaceSwap({
  maxSizeMB = 24,
  srOnlyTitle,
}: FaceSwapProps) {
  const t = useTranslations('photo-effects.face-swap');

  const [sourceImageItems, setSourceImageItems] = useState<ImageUploaderValue[]>([]);
  const [sourceImageUrls, setSourceImageUrls] = useState<string[]>([]);
  const [targetImageItems, setTargetImageItems] = useState<ImageUploaderValue[]>([]);
  const [targetImageUrls, setTargetImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);

  // Ref to track if success toast has been shown for current task
  const successToastShownRef = useRef<string | null>(null);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleGenerate = async () => {
    if (sourceImageUrls.length === 0) {
      toast.error(t('errors.no-source-image'));
      return;
    }

    if (targetImageUrls.length === 0) {
      toast.error(t('errors.no-target-image'));
      return;
    }

    if (isCheckSign) {
      return;
    }

    if (!user) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < COST_CREDITS) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    trackPhotoEffects({
      effect: 'face-swap-start',
      credits_used: COST_CREDITS,
    });

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());
    successToastShownRef.current = null; // Reset toast flag

    try {
      // Use fal face-swap model
      const options: any = {
        source_face_url: sourceImageUrls[0],  // Source face to swap in
        target_image_url: targetImageUrls[0], // Target photo
      };

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'face-swap',
          provider: PROVIDER,
          model: MODEL,
          prompt: 'face swap', // Required by API validation
          options,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || 'Failed to create face swap task');
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          const firstImageUrl = imageUrls[0];
          const timestamp = Date.now();
          const newImageId = `${newTaskId}-${timestamp}`;

          const newImage: GeneratedImage = {
            id: newImageId,
            url: firstImageUrl,
          };

          setGeneratedImages([newImage]);
          toast.success(t('success.swapped'));

          trackPhotoEffects({
            effect: 'face-swap',
            credits_used: COST_CREDITS,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Face swap error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;
    let intervalId: NodeJS.Timeout | null = null;

    const pollTask = async (): Promise<boolean> => {
      try {
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          // Clear taskId first to prevent further polling
          setTaskId(null);
          setIsGenerating(false);
          setGenerationStartTime(null);
          setTaskStatus(null);

          if (imageUrls.length === 0) {
            toast.error('No image generated');
          } else {
            const firstImageUrl = imageUrls[0];
            const timestamp = Date.now();
            const newImageId = `${taskId}-${timestamp}`;

            const newImage: GeneratedImage = {
              id: newImageId,
              url: firstImageUrl,
            };

            setGeneratedImages([newImage]);

            // Only show toast once per task
            if (successToastShownRef.current !== taskId) {
              successToastShownRef.current = taskId;
              toast.success(t('success.swapped'));
            }

            trackPhotoEffects({
              effect: 'face-swap',
              credits_used: COST_CREDITS,
            });
          }

          setProgress(100);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    pollTask();

    intervalId = setInterval(async () => {
      if (cancelled || !taskId) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      const completed = await pollTask();
      if (completed && intervalId) {
        clearInterval(intervalId);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [taskId, isGenerating]);

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-face-swap-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.downloaded'));
    } catch (error) {
      console.error('Failed to download image:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingImageId(null);
    }
  };

  const handleSourceImagesChange = useCallback(
    (items: ImageUploaderValue[]) => {
      setSourceImageItems(items);
      const uploadedUrls = items
        .filter((item) => item.status === 'uploaded' && item.url)
        .map((item) => item.url as string);
      setSourceImageUrls(uploadedUrls);
    },
    []
  );

  const handleTargetImagesChange = useCallback(
    (items: ImageUploaderValue[]) => {
      setTargetImageItems(items);
      const uploadedUrls = items
        .filter((item) => item.status === 'uploaded' && item.url)
        .map((item) => item.url as string);
      setTargetImageUrls(uploadedUrls);
    },
    []
  );

  return (
    <section className="container py-16 md:py-24">
      {srOnlyTitle && <h1 className="sr-only">{srOnlyTitle}</h1>}

      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Left Column - Upload & Settings */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                <Sparkles className="h-5 w-5" />
                {t('info.title')}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('info.description')}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Source Image Upload */}
              <div className="space-y-2">
                <Label>{t('upload.source-title')}</Label>
                <ImageUploader
                  allowMultiple={false}
                  maxImages={1}
                  maxSizeMB={maxSizeMB}
                  onChange={handleSourceImagesChange}
                  emptyHint={t('upload.source-hint')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('upload.format')}
                </p>
              </div>

              {/* Target Image Upload */}
              <div className="space-y-2">
                <Label>{t('upload.target-title')}</Label>
                <ImageUploader
                  allowMultiple={false}
                  maxImages={1}
                  maxSizeMB={maxSizeMB}
                  onChange={handleTargetImagesChange}
                  emptyHint={t('upload.target-hint')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('upload.format')}
                </p>
              </div>

              {/* Credits Info */}
              {user && (
                <div className="flex items-center justify-between rounded-lg bg-muted p-4 text-sm">
                  <span className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    {t('credits.cost', { count: COST_CREDITS })}
                  </span>
                  <span className="font-medium">
                    {t('credits.remaining', { count: remainingCredits })}
                  </span>
                </div>
              )}

              {!user && (
                <Link href="/pricing">
                  <Button variant="outline" className="w-full" size="lg">
                    <CreditCard className="mr-2 h-4 w-4" />
                    View Pricing
                  </Button>
                </Link>
              )}

              {/* Generate Button */}
              {isCheckSign ? (
                <Button className="w-full" disabled size="lg">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking account...
                </Button>
              ) : user ? (
                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    sourceImageUrls.length === 0 ||
                    targetImageUrls.length === 0
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.swapping')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('buttons.swap')}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setIsShowSignModal(true)}
                  className="w-full"
                  size="lg"
                >
                  <User className="mr-2 h-4 w-4" />
                  Sign in to Swap Faces
                </Button>
              )}

              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} />
                  {taskStatus && (
                    <p className="text-center text-xs text-muted-foreground">
                      Status: {taskStatus}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Column - Results */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                <ImageIcon className="h-5 w-5" />
                {generatedImages.length > 0 ? t('results.title-swapped') : t('results.title-preview')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {generatedImages.length === 0 ? (
                <div className="space-y-4">
                  <div className="relative min-h-[500px] overflow-hidden rounded-lg border-2 border-dashed flex items-center justify-center">
                    <div className="text-center space-y-4 p-8">
                      <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground/50" />
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          No face swaps yet
                        </p>
                        <p className="text-xs text-muted-foreground max-w-md">
                          Upload a source face and a target photo, then click "Swap Faces" to see the AI magic
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-4">
                      {generatedImages.map((image) => (
                        <div
                          key={image.id}
                          className="group relative overflow-hidden rounded-lg border"
                        >
                          <LazyImage
                            src={image.url}
                            alt="Face swapped photo"
                            className="w-full"
                          />
                          <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownloadImage(image)}
                              disabled={downloadingImageId === image.id}
                            >
                              {downloadingImageId === image.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  {/* Success Message */}
                  <div className="mt-6 rounded-lg border border-green-500/20 bg-gradient-to-r from-green-500/5 to-green-500/10 p-4">
                    <div className="text-center space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        {t('results.success-title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('results.success-description')}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
